/* eslint-disable max-nested-callbacks */
import { VirtualElement } from '@popperjs/core';
import * as d3 from 'd3';
import dayjs from 'dayjs';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { ContainerText } from 'src/components/atoms';
import { TimeframeInput } from 'src/graphql';
import { useCache, useElementDimensions } from 'src/hooks';
import {
  appendAxis,
  appendAxisAndTicks,
  appendLines,
  createBandScale,
  createLinearScale,
  createNoDecimalAxisLeft,
  createXAxis,
} from 'src/utils';
import {
  addTime,
  DateFormats,
  diff,
  endOf,
  formatDate,
  TimeUnit,
} from 'src/utils/time';
import {
  ContainerTextContainer,
  CustomXBadge,
  CustomYBadge,
  GraphContainer,
} from '../BarStacked/BarStacked.styles';
import { ChartToolTip } from '../ChartToolTip';
import {
  LineChartAndAxisContainer,
  LineChartSvgContainer,
  ScrollableDiv,
  StyledYAxisTitle,
  XAxisTitle,
} from './LineChart.styles';

interface Props {
  xTitle: string;
  yTitle: string;
  data: {
    x: string;
    line: string;
    y: number;
    color: string;
    tooltipTitle?: string;
  }[];
  timeframe: TimeframeInput;
  dateGroup: 'Month' | 'Quarter';
  pdfIsDownloading?: boolean;
  pdfDownloadWidth?: number;
}

export const LineChart = (props: Props) => {
  const theme = useTheme();
  const container = useRef(null);
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [tooltipData, setTooltipData] = useState<{
    selected: string;
    data: Props['data'][0][];
  }>();

  const { start, end } = props.timeframe;
  const startDate = start;
  const endDate = endOf(end, props.dateGroup.toLowerCase() as TimeUnit);

  let monthsArray: string[] = [];
  let currentDate = dayjs(startDate);
  const emptyState = props.data.every((item) => item.y === 0);

  const timeframe = diff(
    props.timeframe.end,
    props.timeframe.start,
    TimeUnit.months,
  );
  const timeFrameLessThanYear = timeframe + 1 <= 12;

  while (currentDate.isBefore(endDate)) {
    const month = currentDate;
    if (props.dateGroup === 'Month') {
      timeFrameLessThanYear
        ? monthsArray.push(formatDate(month, DateFormats.MONTH, false))
        : monthsArray.push(formatDate(month, DateFormats.MM_YY, false));

      currentDate = dayjs(addTime(1, TimeUnit.months, currentDate.toDate()));
    }
    if (props.dateGroup === 'Quarter') {
      monthsArray.push(`Q${formatDate(month, DateFormats.Q_YY)}`);
      currentDate.add(1, 'Q');
      currentDate = dayjs(addTime(1, TimeUnit.quarters, currentDate.toDate()));
    }
  }

  const graph = useRef(null);
  const cache = useCache(props.data);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(svgContainerRef);
  const minLineSpacing = 100;
  const minWidth = 900;
  const height = 500;
  const AXIS_LABEL_WIDTH = 90;
  const minGraphWidth = Math.max(containerWidth, minWidth);
  const margin = { top: 10, right: 20, bottom: 55, left: 40 };
  const [graphWidth, setGraphWidth] = useState(minGraphWidth - margin.left * 2);
  const [hasScroll, setHasScroll] = useState(false);
  const graphHeight = height - margin.top - margin.bottom;

  // Data Functions
  const y = (d: Props['data'][0]) => d.y;

  const onMouseOver = (event?: MouseEvent, d?: unknown) => {
    if (!emptyState) {
      const dAsString = d as string;
      d3.selectAll(`.vertical-line`).style('opacity', '0');
      d3.selectAll(`.tick-label`).style('fill', 'black');
      d3.selectAll(`.tick-label-box`).style('opacity', '0');
      d3.selectAll(`.vertical-line-I${dAsString?.replace?.('/', '')}`).style(
        'opacity',
        '1',
      );
      d3.selectAll(`.tick-label-I${dAsString?.replace?.('/', '')}`).style(
        'fill',
        'white',
      );
      d3.selectAll(`.tick-label-box-I${dAsString?.replace?.('/', '')}`).style(
        'opacity',
        '1',
      );
      if (event) {
        const x = (event.target as Element)?.getBoundingClientRect?.()?.left;
        const y = (event.target as Element)?.getBoundingClientRect?.()?.top;
        if (x && y && d) {
          setCoords?.([x, y]);
          const relevantData = props.data.filter((dat) => dat.x === d);
          setTooltipData?.({
            selected: relevantData[0].tooltipTitle ?? dAsString,
            data: relevantData.sort((a, b) => b.y - a.y),
          });
        }
      }
    }
  };

  const onMouseOut = () => {
    d3.selectAll(`.vertical-line`).style('opacity', '0');
    d3.selectAll(`.tick-label`).style('fill', 'black');
    d3.selectAll(`.tick-label-box`).style('opacity', '0');
    setCoords(null);
  };

  useLayoutEffect(() => {
    const lineGraph = d3.select(graph.current);
    const containerSvg = d3.select(container.current);
    lineGraph.selectAll('*').remove();
    containerSvg.selectAll('*').remove();

    if (monthsArray.length * minLineSpacing < minGraphWidth - margin.left * 2) {
      setHasScroll(false);
      setGraphWidth(minGraphWidth - margin.left * 2);
    } else {
      setHasScroll(true);
      setGraphWidth(monthsArray.length * minLineSpacing);
    }

    const Y = d3.map(cache ?? [], y);
    const xDomain = monthsArray;
    const yMax = emptyState ? 100 : Number(d3.max(Y));
    const yDomainMax = (yMax > 5 ? Math.ceil(yMax / 5) * 5 : yMax) + yMax / 10;

    const { scale: yScale } = createLinearScale({
      startPosition: graphHeight - margin.bottom,
      endPosition: 0,
      domainMax: yDomainMax,
    });
    const { scale: xScale } = createBandScale({
      startPosition: 0,
      endPosition: graphWidth + margin.right,
      domain: xDomain,
    });
    const { Axis: yAxis } = createNoDecimalAxisLeft({
      axisScale: yScale,
      tickNumber: 5,
      tickSize: 0,
      tickPadding: 20,
    });
    const { axis: xAxis } = createXAxis({
      axisScale: xScale as d3.ScaleBand<number>,
      tickSize: 20,
      tickPadding: 10,
      tickSizeOuter: 0,
    });
    const yAxisGrid = d3
      .axisTop(xScale)
      .tickSize(graphHeight - margin.bottom + 20)
      .tickFormat(() => '')
      .ticks(10);

    appendAxisAndTicks({
      graph: containerSvg,
      transform: `translate(${margin.left},23.5)`,
      width: graphWidth + margin.left,
      axis: yAxis,
      dashAxis: 'x2',
      theme,
    });

    appendLines({
      graph: lineGraph,
      data: cache ?? [],
      xScale,
      yScale,
    });
    appendAxis({
      graph: lineGraph,
      transform: `translate(0,${graphHeight - margin.bottom})`,
      axis: xAxis,
      theme,
    });

    lineGraph
      .append('g')
      .attr('transform', `translate(0,${graphHeight - margin.bottom + 20})`)
      .attr('class', 'y axis-grid')
      .call(yAxisGrid)
      .call((g) => g.select('.domain').attr('opacity', 0))
      .call((g) =>
        g
          .selectAll('.tick line')
          .attr('stroke-width', '80')
          .attr('opacity', 0)
          .attr('class', (d) => {
            return `vertical-line-thick vertical-line-thick-I${(
              d as string
            )?.replace?.('/', '')}`;
          })
          .on('mouseover', onMouseOver),
      );
    lineGraph
      .append('g')
      .attr('transform', `translate(0, ${graphHeight - margin.bottom + 20})`)
      .attr('class', 'y axis-grid')
      .call(yAxisGrid)
      .call((g) => g.select('.domain').attr('opacity', 0))
      .call((g) =>
        g
          .selectAll('.tick line')
          .attr('fill', theme.atlas.semantic.color.type.default)
          .attr('opacity', 0)
          .attr('class', (d) => {
            return `vertical-line vertical-line-I${(d as string)?.replace?.(
              '/',
              '',
            )}`;
          }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cache,
    props.data,
    props.timeframe,
    minGraphWidth,
    containerWidth,
    graphWidth,
  ]);

  return (
    <LineChartAndAxisContainer
      style={{ width: '100%' }}
      onMouseLeave={onMouseOut}
    >
      {coords ? (
        <ChartToolTip
          root={
            {
              getBoundingClientRect: () => ({
                top: coords[1] - 10,
                left: coords[0],
                width: 0,
                height: 0,
              }),
            } as VirtualElement
          }
          chartData={{
            title: `${tooltipData?.selected}`,
            titleCounter: 'Issues',
            data:
              tooltipData?.data.map((dat) => ({
                text: dat.line,
                counter: dat.y,
                dotColor: dat.color,
              })) ?? [],
          }}
          blurScroll
        />
      ) : (
        <div />
      )}
      {emptyState && (
        <ContainerTextContainer
          width={minGraphWidth - AXIS_LABEL_WIDTH}
          marginLeft={AXIS_LABEL_WIDTH}
        >
          <ContainerText text="No Data" icon={true} />
        </ContainerTextContainer>
      )}
      <GraphContainer>
        <StyledYAxisTitle>
          <CustomYBadge variant="new" text={props.yTitle} capitalize />
        </StyledYAxisTitle>
        <LineChartSvgContainer
          ref={svgContainerRef}
          width={
            props.pdfIsDownloading
              ? `${props.pdfDownloadWidth ?? 1000}px`
              : `100%`
          }
        >
          <svg
            viewBox={`0 0 ${minGraphWidth - margin.right} ${
              graphHeight + margin.top * 2
            }`}
            height={`${graphHeight + margin.top * 2}px`}
            width={`${minGraphWidth - margin.right}px`}
            preserveAspectRatio="none"
          >
            <g ref={container} />
          </svg>
          <ScrollableDiv
            width={
              props.pdfIsDownloading
                ? `100%`
                : `${minGraphWidth - margin.left - margin.right}px`
            }
            height={`${graphHeight}px`}
            marginTop={`${-graphHeight}px`}
            marginLeft={`${margin.left}px`}
            hasScroll={props.pdfIsDownloading ? false : hasScroll}
          >
            <svg
              viewBox={`0 0 ${graphWidth + margin.right} ${graphHeight}`}
              width={
                props.pdfIsDownloading
                  ? `100%`
                  : `${graphWidth + margin.right}px`
              }
              preserveAspectRatio="none"
              height={`${graphHeight}px`}
            >
              <g ref={graph} />
            </svg>
          </ScrollableDiv>
        </LineChartSvgContainer>
      </GraphContainer>
      <XAxisTitle>
        <CustomXBadge variant="new" text={props.xTitle} capitalize />
      </XAxisTitle>
    </LineChartAndAxisContainer>
  );
};
